// MaterialFuncRegistry — the recipe CATALOGUE: built-in roots + user-recipe lookup seam (D10).
// Recipes are referenced by id (no closed union); user recipes may extend/fork the built-ins but
// never mutate them. The GLSL-bearing registry is PrimitiveRegistry.ts — this file stays GLSL-free.
import type {MaterialRecipe} from "./@MaterialTypes.js";
import {dirtRecipe} from "./Recipes/Recipe_Dirt.js";
import {cobblestoneRecipe} from "./Recipes/Recipe_Cobblestone.js";
import {concreteRecipe} from "./Recipes/Recipe_Concrete.js";
import {marbleRecipe} from "./Recipes/Recipe_Marble.js";
import {sandRecipe} from "./Recipes/Recipe_Sand.js";
import {snowRecipe} from "./Recipes/Recipe_Snow.js";
import {mulchRecipe} from "./Recipes/Recipe_Mulch.js";
import {woodRecipe} from "./Recipes/Recipe_Wood.js";
import {waterRecipe} from "./Recipes/Recipe_Water.js";
import {grassRecipe} from "./Recipes/Recipe_Grass.js";
import {starfieldFullRecipe} from "./Recipes/Recipe_StarfieldFull.js";
import {starfieldRecipe} from "./Recipes/Recipe_Starfield.js";
import {brickRecipe} from "./Recipes/Recipe_Brick.js";
import {plasterRecipe} from "./Recipes/Recipe_Plaster.js";
import {sidingBoardsRecipe} from "./Recipes/Recipe_SidingBoards.js";
import {roofShinglesRecipe} from "./Recipes/Recipe_RoofShingles.js";
import {roofTilesRecipe} from "./Recipes/Recipe_RoofTiles.js";
import {metalStandingSeamRecipe} from "./Recipes/Recipe_MetalStandingSeam.js";
import {glassRecipe} from "./Recipes/Recipe_Glass.js";
import {woodPlanksRecipe} from "./Recipes/Recipe_WoodPlanks.js";

// Order = the plan §9 Phase-8 risk order (concrete → marble → sand → snow → mulch → wood) after
// the Phase-2 slice (dirt, cobblestone).  Callers that pick a "first" recipe (the Phase-7 UI's
// default base) get dirt, so this ordering is load-bearing for the UI default — keep dirt first.
export const builtInRecipes = new Map<string, MaterialRecipe>([
	[dirtRecipe.id, dirtRecipe],
	[cobblestoneRecipe.id, cobblestoneRecipe],
	[concreteRecipe.id, concreteRecipe],
	[marbleRecipe.id, marbleRecipe],
	[sandRecipe.id, sandRecipe],
	[snowRecipe.id, snowRecipe],
	[mulchRecipe.id, mulchRecipe],
	[woodRecipe.id, woodRecipe],
	[woodPlanksRecipe.id, woodPlanksRecipe],
	// Post-plan addition (2026-08-09): a STATIC water surface (no animation — procedural materials
	// must stay deterministic per camera position/zoom, so no time uniform is available).
	[waterRecipe.id, waterRecipe],
	// Post-plan addition (2026-08-11): lawn/mown turf — a dense 3-lattice blade scatter over a dark
	// thatch bed (the mulch "same primitive, per-layer params" stack, with a blade silhouette).
	[grassRecipe.id, grassRecipe],
	// Post-plan addition (2026-08-13): the UNMAPPED-REGION starfield.  `starfield_full` = sky + nebula
	// + milky way + stars + clusters; the DERIVED `starfield` = same minus nebula/milkyWay/clusters.
	// Register full FIRST so the derived recipe can resolve its baseRecipeID against it.
	[starfieldFullRecipe.id, starfieldFullRecipe],
	[starfieldRecipe.id, starfieldRecipe],
	// Buildings Set1 §7.4 (P6, 2026-08-23) — the building-surface recipes.  These back the
	// Building material slots (brick walls, plaster interiors, siding, shingles/tiles/metal
	// roofs, glass).  All are plain recipe extensions of the shared primitive registry — the
	// terrain splat path never references them, but they resolve through the SAME catalogue.
	[brickRecipe.id, brickRecipe],
	[plasterRecipe.id, plasterRecipe],
	[sidingBoardsRecipe.id, sidingBoardsRecipe],
	[roofShinglesRecipe.id, roofShinglesRecipe],
	[roofTilesRecipe.id, roofTilesRecipe],
	[metalStandingSeamRecipe.id, metalStandingSeamRecipe],
	[glassRecipe.id, glassRecipe],
]);

/** Built-in root recipe ids — the 8 plan §4.2 materials (Phase 8 complete) + water + grass + the starfield pair. */
export const BUILT_IN_RECIPE_IDS: string[] = [...builtInRecipes.keys()];

export function GetBuiltInRecipe(id: string): MaterialRecipe | undefined {
	return builtInRecipes.get(id);
}